import React from "react";

import {
  useNavigate,
  useLocation,
  NavigateOptions,
  To,
  Link,
  LinkProps,
} from "react-router-dom";

interface SmartLinkProps extends LinkProps {
  /** Optional: Falls man für einen spezifischen Link die Parameter doch löschen will */
  keepParams?: boolean;
}

export const SmartLink = ({
  to,
  children,
  keepParams = true,
  ...props
}: SmartLinkProps) => {
  const { search } = useLocation();

  // Wenn keepParams false ist oder keine Parameter da sind, normaler Link
  if (!keepParams || !search) {
    return (
      <Link to={to} {...props}>
        {children}
      </Link>
    );
  }

  // Ziel-URL Logik (analog zum Hook)
  const getTarget = () => {
    let pathname = "";
    let targetSearch = "";

    if (typeof to === "string") {
      [pathname, targetSearch] = to.split("?");
    } else {
      pathname = to.pathname || "";
      targetSearch = to.search?.replace(/^\?/, "") || "";
    }

    const finalParams = new URLSearchParams(search);

    // Ziel-Parameter (aus dem 'to' Prop) haben Vorrang
    if (targetSearch) {
      const newParams = new URLSearchParams(targetSearch);
      newParams.forEach((value, key) => {
        finalParams.set(key, value);
      });
    }

    // Rückgabe als Location-Objekt für den Link
    return {
      ...(typeof to === "object" ? to : {}),
      pathname,
      search: `?${finalParams.toString()}`,
    };
  };

  return (
    <Link to={getTarget()} {...props}>
      {children}
    </Link>
  );
};
export default SmartLink;

/**
 * Erweitert das standardmäßige Navigate-Interface,
 * um optional das Beibehalten der Parameter zu steuern.
 */
interface SmartNavigateOptions extends NavigateOptions {
  keepParams?: boolean;
}

export const useSmartNavigate = () => {
  const navigate = useNavigate();
  const { search } = useLocation();

  /**
   * @param to - Das Ziel (String oder To-Objekt)
   * @param options - Die Standard-Optionen von react-router + keepParams
   */
  const smartNavigate = (
    to: To,
    options: SmartNavigateOptions = { keepParams: true },
  ): void => {
    const { keepParams = true, ...routerOptions } = options;

    // Falls Parameter nicht beibehalten werden sollen, Standard-Navigation
    if (!keepParams || !search) {
      navigate(to, routerOptions);
      return;
    }

    // Ziel-Pfad auflösen
    const path = typeof to === "string" ? to : to.pathname || "";

    // Bestehende Parameter aus der URL (Google Ads etc.)
    const currentParams = new URLSearchParams(search);

    // Falls das Ziel bereits eigene Parameter hat, müssen wir mergen
    const [pathname, existingSearch] = path.split("?");
    if (existingSearch) {
      const newParams = new URLSearchParams(existingSearch);
      newParams.forEach((value, key) => {
        currentParams.set(key, value); // Ziel-Parameter überschreiben bestehende
      });
    }

    const finalPath = `${pathname}?${currentParams.toString()}`;

    navigate(finalPath, routerOptions);
  };

  return smartNavigate;
};

export const useSmartPath = () => {
  const { search } = useLocation();

  /**
   * @param to - Das Ziel (String oder To-Objekt)
   * @param options - Die Standard-Optionen von react-router + keepParams
   */
  const smartLink = (to: To): string => {
    // Ziel-Pfad auflösen
    const path = typeof to === "string" ? to : to.pathname || "";

    // Bestehende Parameter aus der URL (Google Ads etc.)
    const currentParams = new URLSearchParams(search);

    // Falls das Ziel bereits eigene Parameter hat, müssen wir mergen
    const [pathname, existingSearch] = path.split("?");
    if (existingSearch) {
      const newParams = new URLSearchParams(existingSearch);
      newParams.forEach((value, key) => {
        currentParams.set(key, value); // Ziel-Parameter überschreiben bestehende
      });
    }

    const finalPath = `${pathname}?${currentParams.toString()}`;

    return finalPath;
  };

  return smartLink;
};
